import React, { useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Skeleton,
  Tab,
  Tabs,
  Typography,
  Avatar,
} from "@mui/material";
import {
  Phone,
  Email,
  Event,
  Note,
  Notifications,
  CheckCircle,
  Schedule,
  ArrowForward,
} from "@mui/icons-material";
import { format, isToday, isTomorrow, isValid, parseISO, startOfDay } from "date-fns";
import { COLORS } from "./constants";
import { useHistory } from "react-router-dom";

const ACTIVITY_ICONS = {
  call: Phone,
  email: Email,
  meeting: Event,
  note: Note,
  reminder: Notifications,
  task: CheckCircle,
};

const ACTIVITY_COLORS = {
  call: "#e3f2fd",
  email: "#e8f5e9",
  meeting: "#fff3e0",
  note: "#f3e5f5",
  reminder: "#fff9c4",
  task: "#e0f7fa",
};

function getActivityDate(item) {
  const raw = item.start_time || item.date || item.created_at;
  if (!raw) return null;
  try {
    const parsed = typeof raw === "string" ? parseISO(raw) : new Date(raw);
    return isValid(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function getActivityType(item) {
  if (item.duration !== undefined || item.direction !== undefined) return "call";
  const raw =
    item.activity_name ||
    item.type_name ||
    item.activity_type_name ||
    item.activity_type?.name ||
    item.type?.name ||
    item.type;
  return typeof raw === "string" ? raw.toLowerCase().trim() : "";
}

function getContactFromMap(item, contactsMap) {
  if (item.contact && typeof item.contact === "object") return item.contact;
  const safeMap = contactsMap ?? {};
  const contactId = item.contact_id ?? item.contact;
  if (contactId == null) return null;
  return safeMap[contactId] ?? safeMap[String(contactId)] ?? safeMap[Number(contactId)] ?? null;
}

function getContactName(item, contactsMap) {
  const contact = getContactFromMap(item, contactsMap);
  const fullName = contact
    ? [contact.first_name, contact.last_name].filter(Boolean).join(" ")
    : "";
  return item.contact_name || contact?.name || fullName || item.name || "Contact";
}

function getCompanyName(item, contactsMap) {
  const contact = getContactFromMap(item, contactsMap);
  return (
    item.company_name ||
    item.contact_company_name ||
    contact?.company_name ||
    contact?.company ||
    ""
  );
}

function avatarColor(name) {
  const COLORS_LIST = ["#5c6bc0", "#26a69a", "#ef5350", "#ffa726", "#66bb6a", "#ab47bc"];
  if (!name) return COLORS_LIST[0];
  let n = 0;
  for (let i = 0; i < name.length; i++) n += name.charCodeAt(i);
  return COLORS_LIST[n % COLORS_LIST.length];
}

function initials(name) {
  if (!name) return "?";
  const parts = name.trim().split(" ");
  return (parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "");
}

function StatusPill({ status }) {
  const lower = (status || "").toLowerCase();
  const map = {
    answered: { color: "success", label: "Answered" },
    missed: { color: "error", label: "Missed" },
    "no answer": { color: "warning", label: "No Answer" },
    busy: { color: "warning", label: "Busy" },
    completed: { color: "success", label: "Completed" },
    scheduled: { color: "primary", label: "Scheduled" }
  };
  const cfg = map[lower] || { color: "default", label: status || "—" };
  return (
    <Chip
      label={cfg.label}
      size="small"
      color={cfg.color}
      sx={{ borderRadius: "12px", fontWeight: 600, fontSize: "0.7rem", height: 22 }}
    />
  );
}

function ActivityRow({ item, type, contactsMap }) {
  const activityType = type || getActivityType(item);
  const IconComp = ACTIVITY_ICONS[activityType] ?? Note;
  const iconBg = ACTIVITY_COLORS[activityType] ?? "#f5f5f5";
  const dt = getActivityDate(item);
  const contactName = item.contact_name || item.contact?.name || "—";
  const companyName = getCompanyName(item, contactsMap);
  const assigneeName = item.assigned_to_name || item.assigned_to?.first_name || "";
  
  const isCallObj = activityType === "call" && item.duration !== undefined;
  
  const formatDuration = (val) => {
    if (!val) return "0m 0s";
    if (typeof val === "string" && val.includes(":")) return val;
    const secs = Number(val);
    if (isNaN(secs)) return String(val);
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}m ${s}s`;
  };

  let activityTitle = item.title || item.notes || item.activity_name || "Activity";
  if (isCallObj) {
      const dir = item.direction ? item.direction.charAt(0).toUpperCase() + item.direction.slice(1) : "Call";
      activityTitle = `${dir} call - ${formatDuration(item.duration)}`;
  }

  return (
    <Box
      display="flex"
      alignItems="flex-start"
      gap={1.5}
      py={1.25}
      sx={{ "&:not(:last-child)": { borderBottom: "1px solid", borderColor: "divider" } }}
    >
      <Avatar
        sx={{
          width: 34,
          height: 34,
          fontSize: "0.72rem",
          fontWeight: 700,
          bgcolor: avatarColor(contactName),
          flexShrink: 0,
        }}
      >
        {initials(contactName)}
      </Avatar>
      <Box
        sx={{
          width: 30,
          height: 30,
          borderRadius: "8px",
          bgcolor: iconBg,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <IconComp sx={{ fontSize: 16, color: "text.secondary" }} />
      </Box>
      <Box flex={1} minWidth={0}>
        <Typography
          variant="body2"
          fontWeight={600}
          noWrap
          sx={{ color: "text.primary", fontSize: "0.8rem" }}
        >
          {activityTitle}
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap>
          {companyName || contactName}
          {assigneeName && (
            <Box
              component="span"
              sx={{
                display: "inline-block",
                ml: 1,
                px: 1,
                py: 0.1,
                bgcolor: avatarColor(assigneeName) + "22",
                borderRadius: "10px",
                color: avatarColor(assigneeName),
                fontWeight: 600,
              }}
            >
              {assigneeName}
            </Box>
          )}
        </Typography>
      </Box>
      <Box textAlign="right" flexShrink={0}>
        <Typography variant="caption" color="text.secondary" display="block">
          {dt ? format(dt, "h:mm a") : "—"}
        </Typography>
        <StatusPill status={item.outcome || item.status} />
      </Box>
    </Box>
  );
}

function GroupSection({ label, count, items, type, contactsMap }) {
  if (!items.length) return null;
  return (
    <Box mb={1}>
      <Typography
        variant="caption"
        fontWeight={700}
        color="text.secondary"
        sx={{ textTransform: "uppercase", letterSpacing: 0.8, fontSize: "0.68rem" }}
      >
        {label} · {count} Activities
      </Typography>
      {items.map((item) => (
        <ActivityRow key={item.id} item={item} type={type || (item.activity_name || "").toLowerCase()} contactsMap={contactsMap} />
      ))}
    </Box>
  );
}

const TABS = ["All", "Tasks", "Meetings"];

export default function UpcomingActivitiesPanel({
  activities = [],
  contactsMap = {},
  isLoading,
  activeTab = 0,
  onTabChange,
}) {
  const history = useHistory();

  const grouped = useMemo(() => {
    const filterFn = (items, type) => {
      if (type === "all") return items;
      return items.filter((a) => getActivityType(a).includes(type));
    };

    const tabType = ["all", "task", "meeting"][activeTab] || "all";
    const source = filterFn(activities, tabType);

    const pastItems = [];
    const todayItems = [];
    const tomorrowItems = [];
    const laterItems = [];
    const startOfToday = startOfDay(new Date());

    source.forEach((item) => {
      const dt = getActivityDate(item);
      if (!dt || dt < startOfToday) { 
        pastItems.push(item); 
        return; 
      }
      if (isToday(dt)) todayItems.push(item);
      else if (isTomorrow(dt)) tomorrowItems.push(item);
      else laterItems.push(item);
    });

    return { pastItems, todayItems, tomorrowItems, laterItems };
  }, [activities, activeTab]);

  const total = grouped.pastItems.length + grouped.todayItems.length + grouped.tomorrowItems.length + grouped.laterItems.length;

  return (
    <Card sx={{ borderRadius: "16px", boxShadow: "0 2px 12px rgba(0,0,0,0.07)", height: "100%" }}>
      <CardContent sx={{ p: 2.5, pb: "12px !important" }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
          <Typography variant="h6" fontWeight={700} sx={{ fontSize: "0.95rem" }}>
            Upcoming Activities
          </Typography>
          <Typography
            variant="caption"
            component="span"
            onClick={() => history.push("/admin/crm/activities")}
            sx={{ cursor: "pointer", color: COLORS.indigo, fontWeight: 600, display: "flex", alignItems: "center", gap: 0.3 }}
          >
            View all <ArrowForward sx={{ fontSize: 12 }} />
          </Typography>
        </Box>

        <Tabs
          value={activeTab}
          onChange={(_, v) => { if (onTabChange) onTabChange(v); }}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            mb: 1,
            minHeight: 36,
            "& .MuiTab-root": { minHeight: 36, fontSize: "0.75rem", fontWeight: 600, py: 0.5 },
            "& .MuiTabs-indicator": { backgroundColor: COLORS.indigo },
            "& .Mui-selected": { color: `${COLORS.indigo} !important` },
          }}
        >
          {TABS.map((t) => (
            <Tab key={t} label={t} />
          ))}
        </Tabs>

        <Divider sx={{ mb: 1 }} />

        {isLoading ? (
          Array.from({ length: 4 }).map((_, i) => (
            <Box key={i} display="flex" gap={1.5} mb={1.5}>
              <Skeleton variant="circular" width={34} height={34} />
              <Box flex={1}>
                <Skeleton variant="text" width="70%" />
                <Skeleton variant="text" width="50%" />
              </Box>
            </Box>
          ))
        ) : total === 0 ? (
          <Box py={4} textAlign="center">
            <Schedule sx={{ fontSize: 40, color: "text.disabled", mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              No activities for this period
            </Typography>
          </Box>
        ) : (
          <Box sx={{ maxHeight: 380, overflowY: "auto" }}>
            <GroupSection
              label="Past / Overdue"
              count={grouped.pastItems.length}
              items={grouped.pastItems}
              contactsMap={contactsMap}
            />
            <GroupSection
              label="Today"
              count={grouped.todayItems.length}
              items={grouped.todayItems}
              contactsMap={contactsMap}
            />
            <GroupSection
              label="Tomorrow"
              count={grouped.tomorrowItems.length}
              items={grouped.tomorrowItems}
              contactsMap={contactsMap}
            />
            <GroupSection
              label="Upcoming"
              count={grouped.laterItems.length}
              items={grouped.laterItems}
              contactsMap={contactsMap}
            />
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
